import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SupportPanel from '@/components/SupportPanel';
import { toast } from 'sonner';

import { API } from '../config';
const opts = { withCredentials: true };

// Mi Cuenta: monedero/inversión, planes y pagos BSC, link de referido,
// historial de inversión y canal de soporte — todo en una página.
const Cuenta = () => {
  const [wallet, setWallet] = useState(null);
  const [ref, setRef] = useState(null);
  const [hist, setHist] = useState(null);
  const [info, setInfo] = useState(null);
  const [dep, setDep] = useState('15');
  const [pago, setPago] = useState(null);
  const [tx, setTx] = useState('');
  const [paper, setPaper] = useState(null);
  const [reseteando, setReseteando] = useState(false);
  const [statements, setStatements] = useState([]);

  const cargar = async () => {
    const s = async (fn) => { try { return (await fn()).data; } catch { return null; } };
    setWallet(await s(() => axios.get(`${API}/wallet`, opts)));
    setRef(await s(() => axios.get(`${API}/referrals/my-code`, opts)));
    setHist(await s(() => axios.get(`${API}/wallet/history?group=day`, opts)));
    setInfo(await s(() => axios.get(`${API}/payment/info`, opts)));
    setPaper(await s(() => axios.get(`${API}/paper/balance`, opts)));
    setStatements((await s(() => axios.get(`${API}/investment/statements`, opts)))?.statements || []);
  };
  useEffect(() => { cargar(); }, []);

  const resetearPaper = async () => {
    if (!window.confirm('¿Resetear tu saldo ficticio a $10,000? No borra tu historial de operaciones paper, solo el saldo disponible para seguir practicando.')) return;
    setReseteando(true);
    try {
      await axios.post(`${API}/paper/reset`, {}, opts);
      toast.success('Saldo ficticio reseteado a $10,000');
      cargar();
    } catch {
      toast.error('Error al resetear el saldo ficticio');
    } finally {
      setReseteando(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-[#1E1E1E] border-[#27272A]">
          <CardHeader><CardTitle className="text-white text-base">💼 Mi inversión (monedero)</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <p className="text-3xl font-bold text-[#00FF88]">${(wallet?.balance ?? 0).toFixed(2)}</p>
            <p className="text-[#A1A1AA]">Cuotas pagadas (20% sobre ganancias): ${(wallet?.total_fees_paid ?? 0).toFixed(2)} · Ganado por referidos: ${(wallet?.total_referral_earned ?? 0).toFixed(2)}</p>
            <p className="text-[#A1A1AA] text-xs">Si tu inversión llega a $0, las operaciones REALES se pausan hasta nuevo depósito. Paper siempre gratis.</p>
          </CardContent>
        </Card>

        <Card className="bg-[#1E1E1E] border-[#27272A]">
          <CardHeader><CardTitle className="text-white text-base">🎮 Mi saldo ficticio (paper)</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <p className={`text-3xl font-bold ${(paper?.saldo_actual ?? 10000) < 0 ? 'text-[#FF3B30]' : 'text-[#00D4FF]'}`}>
              ${(paper?.saldo_actual ?? 10000).toFixed(2)}
            </p>
            <p className="text-[#A1A1AA] text-xs">De $10,000 iniciales · P&L acumulado en paper: ${(paper?.pnl_acumulado ?? 0).toFixed(2)}</p>
            <button
              onClick={resetearPaper}
              disabled={reseteando}
              className="px-3 py-1.5 rounded text-xs font-bold bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/20 hover:bg-[#00D4FF]/20 transition-all disabled:opacity-50"
            >
              {reseteando ? 'Reseteando...' : 'Resetear a $10,000'}
            </button>
          </CardContent>
        </Card>

        <Card className="bg-[#1E1E1E] border-[#27272A]">
          <CardHeader><CardTitle className="text-white text-base">🔗 Mi link de referido (ganas el 5%)</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <p className="text-[#00D4FF] mono break-all">{ref?.referral_link || '...'}</p>
            <button onClick={(e) => { navigator.clipboard.writeText(ref?.referral_link || ''); e.target.textContent = 'Copiado!'; setTimeout(() => e.target.textContent = 'Copiar link', 2000); }}
              className="px-3 py-1 rounded bg-[#00FF88] text-black text-xs font-bold active:scale-95 transition-transform">Copiar link</button>
          </CardContent>
        </Card>

        <Card className="bg-[#1E1E1E] border-[#27272A]">
          <CardHeader><CardTitle className="text-white text-base">💳 Inversión (BNB Smart Chain)</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className={`p-2 rounded text-xs ${info?.investment_active ? 'bg-[#00FF88]/10 text-[#00FF88]' : 'bg-red-500/10 text-red-400'}`}>
              {info?.investment_active ? 'Plataforma ACTIVA' : 'Inversión requerida para operar en REAL'}
              {info?.monthly_expires && <span className="text-[#A1A1AA] ml-2">· Vence: {new Date(info.monthly_expires).toLocaleDateString()}</span>}
            </div>
            {info?.total_invested > 0 && (
              <div className="flex gap-3 text-xs text-[#A1A1AA]">
                <span>Invertido: <b className="text-white">${info.total_invested.toFixed(2)}</b></span>
                <span>20% pagado: <b className={info.total_fees_paid >= info.total_invested ? 'text-red-400' : 'text-[#00FF88]'}>${info.total_fees_paid.toFixed(2)}</b></span>
              </div>
            )}
            <p className="text-[#A1A1AA] text-xs">Mínimo $15 USDT: $5 cuota mensual + $10 saldo operativo. Paper siempre gratis.</p>
            <div className="flex gap-2 items-center">
              <span className="text-[#A1A1AA] text-xs">$</span>
              <input value={dep} onChange={(e) => setDep(e.target.value)} placeholder="15" type="number" min="15"
                className="bg-[#111] border border-[#27272A] rounded px-2 py-1 text-white w-24" />
              <span className="text-[#A1A1AA] text-xs">USDT</span>
              <button onClick={async () => {
                try {
                  setPago((await axios.post(`${API}/payment/request?amount=${dep}`, {}, opts)).data);
                } catch(e) { alert(e.response?.data?.detail || 'Error'); }
              }} className="px-3 py-1 rounded bg-[#00D4FF] text-black text-xs font-bold">Invertir</button>
            </div>
            {pago && (<div className="bg-[#111] rounded p-3 space-y-2">
              <p className="text-white">Envía: <b className="text-[#00FF88]">${pago.amount_usd}</b> USDT (BEP-20)</p>
              <p className="text-[#A1A1AA] text-xs">Cuota mensual: ${pago.cuota_mensual} · Saldo operativo: ${pago.saldo_operativo}</p>
              {(() => {
                const addr = pago.wallets_de_pago?.BNB_BEP20 || pago.wallets_de_pago?.USDT_BEP20 || Object.values(pago.wallets_de_pago || {})[0];
                return addr ? (
                  <div className="bg-[#1E1E1E] rounded p-2">
                    <p className="text-[#A1A1AA] text-xs mb-1">Wallet BNB (BEP-20) — envía USDT aquí:</p>
                    <div className="flex items-center gap-2">
                      <p className="text-[#00FF88] text-xs font-mono break-all flex-1">{addr}</p>
                      <button onClick={() => navigator.clipboard.writeText(addr)}
                        className="text-[10px] px-2 py-1 rounded bg-[#27272A] text-[#A1A1AA] hover:text-white shrink-0">Copiar</button>
                    </div>
                  </div>
                ) : <p className="text-red-400 text-xs">Wallet no configurada aún. Contacta soporte.</p>;
              })()}
              <div className="flex gap-2">
                <input value={tx} onChange={(e) => setTx(e.target.value)} placeholder="Hash de la transacción (0x...)"
                  className="bg-black border border-[#27272A] rounded px-2 py-1 text-white flex-1 text-xs" />
                <button onClick={async () => { await axios.post(`${API}/payment/submit?request_id=${pago.request_id}&tx_hash=${tx}`, {}, opts); alert('Pago reportado: el admin lo verificará en BscScan'); setPago(null); }}
                  className="px-3 py-1 rounded bg-[#00FF88] text-black text-xs font-bold">Reportar pago</button>
              </div>
            </div>)}
          </CardContent>
        </Card>

        <Card className="bg-[#1E1E1E] border-[#27272A]">
          <CardHeader><CardTitle className="text-white text-base">📊 Historial de inversión (por día)</CardTitle></CardHeader>
          <CardContent className="text-sm max-h-48 overflow-y-auto">
            {hist?.historial?.length ? hist.historial.slice(-10).reverse().map((d) => (
              <div key={d.periodo} className="flex flex-wrap gap-x-3 gap-y-0.5 border-b border-[#27272A] py-1 text-xs">
                <span className="text-white font-medium">{d.periodo}</span>
                {d.depositos > 0 && <span className="text-[#00D4FF]">dep ${d.depositos}</span>}
                {d.gastos_cuotas > 0 && <span className="text-red-400">cuotas -${d.gastos_cuotas}</span>}
                {d.ganancia_trading !== 0 && <span className="text-[#FFD600]">real ${d.ganancia_trading > 0 ? '+' : ''}{d.ganancia_trading}</span>}
                {(d.ganancia_paper ?? 0) !== 0 && <span className="text-[#00D4FF]">paper ${d.ganancia_paper > 0 ? '+' : ''}{d.ganancia_paper}</span>}
                {d.ganancia_referidos > 0 && <span className="text-purple-400">ref +${d.ganancia_referidos}</span>}
                <span className={`font-bold ${d.neto >= 0 ? 'text-[#00FF88]' : 'text-red-400'}`}>neto ${d.neto >= 0 ? '+' : ''}{d.neto}</span>
              </div>
            )) : <p className="text-[#A1A1AA]">Sin movimientos todavía</p>}
          </CardContent>
        </Card>

        {statements.length > 0 && (
          <Card className="bg-[#1E1E1E] border-[#27272A]">
            <CardHeader><CardTitle className="text-white text-base">📄 Estados de cuenta</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2 max-h-48 overflow-y-auto">
              {statements.map((st) => (
                <div key={st.statement_id} className="flex items-center justify-between border-b border-[#27272A] py-1.5 text-xs">
                  <div>
                    <span className="text-white">{new Date(st.generated_at).toLocaleDateString()}</span>
                    <span className="text-[#A1A1AA] ml-2">
                      {st.motivo === 'agotado' ? 'crédito agotado' : 'nueva inversión'} ·
                      P&L: <span className={st.pnl_real_periodo >= 0 ? 'text-[#00FF88]' : 'text-red-400'}>${st.pnl_real_periodo}</span>
                    </span>
                  </div>
                  <a href={`${API}/investment/statements/${st.statement_id}/download`}
                    className="px-2 py-1 rounded bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/20 hover:bg-[#00D4FF]/20 text-[10px] font-bold">
                    Descargar CSV
                  </a>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card className="bg-[#1E1E1E] border-[#27272A] lg:col-span-2">
          <CardHeader><CardTitle className="text-white text-base">🛟 Soporte</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <SupportPanel />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Cuenta;